import React, { useEffect, useState, useCallback } from "react";
import {
  FiStar,
  FiHome,
  FiCompass,
  FiTrash2,
  FiSearch,
  FiRotateCcw,
  FiCheckSquare,
  FiSquare,
  FiMessageSquare,
  FiAlertTriangle,
  FiCheckCircle,
} from "react-icons/fi";
import Swal from "sweetalert2";
import { getApiBaseUrl } from "../utils/apiConfig";
import "./AdminReviewManagement.css";

const API = getApiBaseUrl();

export default function AdminReviewManagement() {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({
    totalReviews: 0,
    hotelReviews: 0,
    tourReviews: 0,
    avgRating: 0,
  });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Filters
  const [search, setSearch] = useState("");
  const [hotelNameFilter, setHotelNameFilter] = useState("");
  const [listingNameFilter, setListingNameFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [ratingFilter, setRatingFilter] = useState("");
  const [page, setPage] = useState(1);
  const limit = 25;
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });

  // Selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [expandedCommentId, setExpandedCommentId] = useState(null);

  // Fetch reviews from backend
  const fetchReviews = useCallback(async () => {
    try {
      setLoading(true);
      setErr("");

      const adminToken = localStorage.getItem("adminToken");
      const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};

      const params = new URLSearchParams();
      if (search.trim()) params.append("search", search.trim());
      if (hotelNameFilter.trim()) params.append("hotelName", hotelNameFilter.trim());
      if (listingNameFilter.trim()) params.append("listingName", listingNameFilter.trim());
      if (typeFilter !== "all") params.append("type", typeFilter);
      if (ratingFilter) params.append("rating", ratingFilter);
      params.append("page", page);
      params.append("limit", limit);

      const res = await fetch(`${API}/admin/reviews?${params.toString()}`, {
        headers,
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to load reviews.");
      }

      setReviews(data.reviews || []);
      if (data.stats) setStats(data.stats);
      if (data.pagination) setPagination(data.pagination);
    } catch (e) {
      console.error(e);
      setErr(e.message || "Network error loading reviews.");
    } finally {
      setLoading(false);
    }
  }, [search, hotelNameFilter, listingNameFilter, typeFilter, ratingFilter, page, limit]);

  useEffect(() => {
    fetchReviews();
    setSelectedIds(new Set());
  }, [fetchReviews]);

  // Reset Filters
  const handleResetFilters = () => {
    setSearch("");
    setHotelNameFilter("");
    setListingNameFilter("");
    setTypeFilter("all");
    setRatingFilter("");
    setPage(1);
  };

  // Toggle Single Selection
  const handleToggleSelect = (reviewKey) => {
    const next = new Set(selectedIds);
    if (next.has(reviewKey)) {
      next.delete(reviewKey);
    } else {
      next.add(reviewKey);
    }
    setSelectedIds(next);
  };

  // Select All on Current Page
  const handleSelectAll = () => {
    if (selectedIds.size === reviews.length && reviews.length > 0) {
      setSelectedIds(new Set());
    } else {
      const allKeys = new Set(reviews.map((r) => `${r.type}_${r.rawId}`));
      setSelectedIds(allKeys);
    }
  };

  // Delete Single Review
  const handleDeleteSingle = async (review) => {
    const result = await Swal.fire({
      title: "Delete Review for Inquiry?",
      html: `
        <div style="text-align: left; font-size: 14px; margin-bottom: 12px; color: #475569;">
          <p><strong>Property/Host:</strong> ${review.hotelName}</p>
          <p><strong>Listing:</strong> ${review.listingTitle}</p>
          <p><strong>Reviewer:</strong> ${review.reviewer.name} (${review.rating}★)</p>
          <p style="background: #f8fafc; padding: 8px; border-radius: 6px; border: 1px solid #e2e8f0; font-style: italic;">
            "${review.comment}"
          </p>
        </div>
        <div style="text-align: left;">
          <label style="display: block; font-size: 13px; font-weight: 600; margin-bottom: 4px; color: #1e293b;">
            Inquiry / Deletion Reason (Optional):
          </label>
          <textarea id="swal-inquiry-reason" class="swal2-textarea" style="width: 100%; box-sizing: border-box; height: 80px; margin: 0; font-size: 13px; padding: 8px;" placeholder="e.g. Dispute settled, fraudulent review, policy violation..."></textarea>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete Review",
      confirmButtonColor: "#ef4444",
      cancelButtonText: "Cancel",
      preConfirm: () => {
        const reason = document.getElementById("swal-inquiry-reason")?.value;
        return { reason };
      },
    });

    if (!result.isConfirmed) return;

    try {
      const adminToken = localStorage.getItem("adminToken");
      const res = await fetch(`${API}/admin/reviews/${review.type}/${review.rawId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          ...(adminToken ? { Authorization: `Bearer ${adminToken}` } : {}),
        },
        body: JSON.stringify({ inquiryReason: result.value?.reason }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to delete review.");

      Swal.fire({
        icon: "success",
        title: "Review Deleted",
        text: "The review has been removed and listing metrics have been updated.",
        timer: 2000,
        showConfirmButton: false,
      });

      fetchReviews();
    } catch (e) {
      Swal.fire("Error", e.message, "error");
    }
  };

  // Bulk Delete Selected Reviews
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;

    const selectedReviews = reviews.filter((r) =>
      selectedIds.has(`${r.type}_${r.rawId}`)
    );

    const result = await Swal.fire({
      title: `Delete ${selectedIds.size} Selected Reviews?`,
      html: `
        <p style="font-size: 14px; color: #475569; margin-bottom: 12px;">
          You are about to delete <strong>${selectedIds.size}</strong> reviews across hotels and tours. This action cannot be undone.
        </p>
        <div style="text-align: left;">
          <label style="display: block; font-size: 13px; font-weight: 600; margin-bottom: 4px; color: #1e293b;">
            Inquiry / Deletion Reason (Optional):
          </label>
          <textarea id="swal-bulk-reason" class="swal2-textarea" style="width: 100%; box-sizing: border-box; height: 80px; margin: 0; font-size: 13px; padding: 8px;" placeholder="e.g. Mass dispute inquiry resolution, content moderation..."></textarea>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: `Delete ${selectedIds.size} Reviews`,
      confirmButtonColor: "#ef4444",
      cancelButtonText: "Cancel",
      preConfirm: () => {
        const reason = document.getElementById("swal-bulk-reason")?.value;
        return { reason };
      },
    });

    if (!result.isConfirmed) return;

    try {
      const payload = selectedReviews.map((r) => ({
        type: r.type,
        id: r.rawId,
      }));

      const adminToken = localStorage.getItem("adminToken");
      const res = await fetch(`${API}/admin/reviews/bulk-delete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(adminToken ? { Authorization: `Bearer ${adminToken}` } : {}),
        },
        body: JSON.stringify({
          reviews: payload,
          inquiryReason: result.value?.reason,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to bulk delete reviews.");

      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: `Successfully removed ${data.deletedCount || selectedIds.size} reviews.`,
        timer: 2000,
        showConfirmButton: false,
      });

      setSelectedIds(new Set());
      fetchReviews();
    } catch (e) {
      Swal.fire("Error", e.message, "error");
    }
  };

  const isAllSelected =
    reviews.length > 0 && selectedIds.size === reviews.length;

  return (
    <div className="arm-page">
      {/* Header */}
      <div className="arm-head">
        <div>
          <h2 className="arm-title">Review & Inquiry Moderation</h2>
          <p className="arm-subtitle">
            Search by hotel name or listing title, moderate reviews, and resolve traveler inquiries.
          </p>
        </div>
      </div>

      {/* KPI Stat Cards */}
      <section className="arm-cards">
        <div className="card stat-card">
          <div className="stat-top">
            <span className="stat-title">Total Reviews</span>
            <span className="stat-icon-box blue"><FiMessageSquare /></span>
          </div>
          <div className="stat-value">{stats.totalReviews.toLocaleString()}</div>
          <div className="stat-sub">Across platform listings</div>
        </div>

        <div className="card stat-card">
          <div className="stat-top">
            <span className="stat-title">Hotel Reviews</span>
            <span className="stat-icon-box green"><FiHome /></span>
          </div>
          <div className="stat-value">{stats.hotelReviews.toLocaleString()}</div>
          <div className="stat-sub">Verified stay reviews</div>
        </div>

        <div className="card stat-card">
          <div className="stat-top">
            <span className="stat-title">Tour Reviews</span>
            <span className="stat-icon-box purple"><FiCompass /></span>
          </div>
          <div className="stat-value">{stats.tourReviews.toLocaleString()}</div>
          <div className="stat-sub">Guided tour reviews</div>
        </div>

        <div className="card stat-card">
          <div className="stat-top">
            <span className="stat-title">Platform Avg Rating</span>
            <span className="stat-icon-box amber"><FiStar /></span>
          </div>
          <div className="stat-value">★ {stats.avgRating}</div>
          <div className="stat-sub">Overall guest satisfaction</div>
        </div>
      </section>

      {/* Filter & Search Bar */}
      <div className="card arm-filter-card">
        <div className="arm-filter-grid">
          {/* Global Search */}
          <div className="filter-group">
            <label className="filter-label">Quick Search</label>
            <div className="search-input-wrapper">
              <FiSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search hotel, listing, reviewer, comment..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>
          </div>

          {/* Hotel Name Filter */}
          <div className="filter-group">
            <label className="filter-label">Hotel / Property Name</label>
            <div className="search-input-wrapper">
              <FiHome className="search-icon" />
              <input
                type="text"
                placeholder="e.g. Palm Garden, Grand View..."
                value={hotelNameFilter}
                onChange={(e) => {
                  setHotelNameFilter(e.target.value);
                  setPage(1);
                }}
              />
            </div>
          </div>

          {/* Listing Name Filter */}
          <div className="filter-group">
            <label className="filter-label">Listing / Package Title</label>
            <div className="search-input-wrapper">
              <FiCompass className="search-icon" />
              <input
                type="text"
                placeholder="e.g. Deluxe Room, Sunset Tour..."
                value={listingNameFilter}
                onChange={(e) => {
                  setListingNameFilter(e.target.value);
                  setPage(1);
                }}
              />
            </div>
          </div>

          {/* Rating Filter */}
          <div className="filter-group">
            <label className="filter-label">Rating</label>
            <select
              value={ratingFilter}
              onChange={(e) => {
                setRatingFilter(e.target.value);
                setPage(1);
              }}
              className="filter-select"
            >
              <option value="">All Ratings</option>
              <option value="5">⭐⭐⭐⭐⭐ 5 Stars</option>
              <option value="4">⭐⭐⭐⭐ 4 Stars</option>
              <option value="3">⭐⭐⭐ 3 Stars</option>
              <option value="2">⭐⭐ 2 Stars</option>
              <option value="1">⭐ 1 Star</option>
            </select>
          </div>

          {/* Reset Filters */}
          <div className="filter-group filter-actions">
            <button className="btn-reset" onClick={handleResetFilters} title="Reset all filters">
              <FiRotateCcw /> Reset
            </button>
          </div>
        </div>

        {/* Category Segmented Selector */}
        <div className="arm-category-bar">
          <div className="segmented-control">
            <button
              className={`seg-btn ${typeFilter === "all" ? "active" : ""}`}
              onClick={() => {
                setTypeFilter("all");
                setPage(1);
              }}
            >
              All Types ({stats.totalReviews})
            </button>
            <button
              className={`seg-btn ${typeFilter === "hotel" ? "active" : ""}`}
              onClick={() => {
                setTypeFilter("hotel");
                setPage(1);
              }}
            >
              🏨 Hotels ({stats.hotelReviews})
            </button>
            <button
              className={`seg-btn ${typeFilter === "tour" ? "active" : ""}`}
              onClick={() => {
                setTypeFilter("tour");
                setPage(1);
              }}
            >
              🎒 Tours ({stats.tourReviews})
            </button>
          </div>

          {/* Batch Selection Action Badge */}
          {selectedIds.size > 0 && (
            <div className="batch-action-bar">
              <span className="batch-badge">
                <FiCheckCircle /> {selectedIds.size} Selected
              </span>
              <button className="btn-bulk-delete" onClick={handleBulkDelete}>
                <FiTrash2 /> Delete Selected ({selectedIds.size})
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main Reviews Table */}
      <div className="card arm-table-card">
        {loading ? (
          <div className="arm-loading-state">
            <div className="arm-spinner"></div>
            <p>Loading review moderation data...</p>
          </div>
        ) : err ? (
          <div className="arm-error-state">
            <FiAlertTriangle className="error-icon" />
            <p>{err}</p>
            <button className="btn-reset" onClick={fetchReviews}>Retry</button>
          </div>
        ) : reviews.length === 0 ? (
          <div className="arm-empty-state">
            <FiMessageSquare className="empty-icon" />
            <h3>No Reviews Found</h3>
            <p>No reviews match your current search or filter criteria.</p>
            <button className="btn-reset" onClick={handleResetFilters}>Clear Filters</button>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="arm-table">
              <thead>
                <tr>
                  <th style={{ width: "40px" }}>
                    <button
                      className="select-all-btn"
                      onClick={handleSelectAll}
                      title={isAllSelected ? "Deselect All" : "Select All"}
                    >
                      {isAllSelected ? <FiCheckSquare className="checked-icon" /> : <FiSquare />}
                    </button>
                  </th>
                  <th>Category</th>
                  <th>Hotel / Host Name</th>
                  <th>Listing Title</th>
                  <th>Reviewer</th>
                  <th>Rating</th>
                  <th>Review Comment</th>
                  <th>Date</th>
                  <th style={{ textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reviews.map((rev) => {
                  const key = `${rev.type}_${rev.rawId}`;
                  const isSelected = selectedIds.has(key);
                  const isExpanded = expandedCommentId === key;

                  return (
                    <tr key={key} className={isSelected ? "row-selected" : ""}>
                      {/* Checkbox */}
                      <td>
                        <button
                          className="select-row-btn"
                          onClick={() => handleToggleSelect(key)}
                        >
                          {isSelected ? (
                            <FiCheckSquare className="checked-icon" />
                          ) : (
                            <FiSquare />
                          )}
                        </button>
                      </td>

                      {/* Category Badge */}
                      <td>
                        <span className={`cat-badge ${rev.type}`}>
                          {rev.type === "hotel" ? "🏨 Hotel" : "🎒 Tour"}
                        </span>
                      </td>

                      {/* Hotel Name */}
                      <td>
                        <strong className="hotel-name-text">{rev.hotelName}</strong>
                      </td>

                      {/* Listing Title */}
                      <td>
                        <span className="listing-title-text">{rev.listingTitle}</span>
                      </td>

                      {/* Reviewer Details */}
                      <td>
                        <div className="reviewer-cell">
                          <div className="reviewer-avatar-circle">
                            {rev.reviewer.name.charAt(0)}
                          </div>
                          <div>
                            <div className="reviewer-name">{rev.reviewer.name}</div>
                            <div className="reviewer-email">{rev.reviewer.email}</div>
                          </div>
                        </div>
                      </td>

                      {/* Rating Badge */}
                      <td>
                        <div className="rating-badge-container">
                          <span className={`rating-pill r-${rev.rating}`}>
                            ★ {rev.rating}
                          </span>
                          <span className="rating-text-label">{rev.ratingLabel}</span>
                        </div>
                      </td>

                      {/* Comment */}
                      <td className="comment-cell">
                        <p className="comment-text">
                          "{isExpanded ? rev.comment : (rev.comment.length > 85 ? rev.comment.substring(0, 85) + "..." : rev.comment)}"
                        </p>
                        {rev.comment.length > 85 && (
                          <button
                            className="btn-expand-comment"
                            onClick={() => setExpandedCommentId(isExpanded ? null : key)}
                          >
                            {isExpanded ? "Show less" : "Read full"}
                          </button>
                        )}
                      </td>

                      {/* Date */}
                      <td className="date-cell">
                        {new Date(rev.createdAt).toLocaleDateString()}
                      </td>

                      {/* Actions */}
                      <td style={{ textAlign: "center" }}>
                        <button
                          className="btn-delete-action"
                          onClick={() => handleDeleteSingle(rev)}
                          title="Delete review for inquiry"
                        >
                          <FiTrash2 /> Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Footer */}
        {pagination.totalPages > 1 && (
          <div className="arm-pagination">
            <span className="pagination-info">
              Showing {(page - 1) * limit + 1} - {Math.min(page * limit, pagination.total)} of {pagination.total} reviews
            </span>
            <div className="pagination-buttons">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="btn-page"
              >
                Previous
              </button>
              <span className="page-number-indicator">
                Page {page} of {pagination.totalPages}
              </span>
              <button
                disabled={page >= pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="btn-page"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
